/**
 * Wedly category, subcategory, and listing-field configuration for Create Listing.
 * Drives dynamic form inputs, photo slot guidance, and AI-oriented search metadata.
 */

const opt = (value, label) => ({ value, label });

/** @typedef {'text'|'textarea'|'select'|'multiSelect'|'toggle'|'number'|'radio'|'chips'} WedlyFieldType */

/**
 * @typedef {Object} WedlyFieldDef
 * @property {string} key
 * @property {string} label
 * @property {WedlyFieldType} type
 * @property {Array<{value: string, label: string}>} [options]
 * @property {boolean} [required]
 * @property {string} [placeholder]
 * @property {string} [searchFacet] style|theme|colour|size|material|brand|occasion|condition|weddingStyle
 * @property {string[]} [categoryIds] limit to parent and/or subcategory ids; omit = parent category only
 */

/**
 * @typedef {Object} WedlyCategoryDef
 * @property {string} id
 * @property {string} name
 * @property {Array<{id: string, name: string}>} subcategories
 * @property {WedlyFieldDef[]} fields
 * @property {string[]} [requiredImageSlots]
 */

const CONDITION_STANDARD = [
  opt('new', 'New'),
  opt('like-new', 'Like New'),
  opt('used-once', 'Used Once'),
  opt('well-loved', 'Well Loved'),
];

const CONDITION_WEDDING_DRESS = [
  opt('new', 'New'),
  opt('like-new', 'Like New'),
  opt('used-once', 'Used Once'),
  opt('well-loved', 'Well Loved'),
];

const CONDITION_SHOES = [
  opt('worn-once', 'Worn Once'),
  opt('minor-wear', 'Minor Wear'),
  opt('new', 'New'),
  opt('professionally-cleaned', 'Professionally Cleaned'),
];

const CONDITION_FLOWERS = [
  opt('used-once', 'Used Once'),
  opt('handmade', 'Handmade'),
  opt('like-new', 'Like New'),
];

const CONDITION_ACCESSORIES = [
  opt('new', 'New'),
  opt('used-once', 'Used Once'),
  opt('professionally-cleaned', 'Professionally Cleaned'),
];

/** @type {WedlyCategoryDef[]} */
export const WEDLY_CATEGORY_DEFINITIONS = [
  {
    id: 'hens-bachelorettes',
    name: 'Hens / Bachelorettes',
    subcategories: [
      { id: 'decorations', name: 'Decorations' },
      { id: 'games', name: 'Games' },
      { id: 'outfits', name: 'Outfits' },
      { id: 'table-styling', name: 'Table Styling' },
      { id: 'balloons-signage', name: 'Balloons & Signage' },
      { id: 'props', name: 'Props' },
      { id: 'accessories', name: 'Accessories' },
      { id: 'party-packs', name: 'Party Packs' },
    ],
    fields: [
      {
        key: 'themeStyle',
        label: 'Theme Style',
        type: 'radio',
        searchFacet: 'style',
        options: [
          opt('boho', 'Boho'),
          opt('coastal', 'Coastal'),
          opt('minimalist', 'Minimalist'),
          opt('luxe', 'Luxe'),
          opt('garden-party', 'Garden Party'),
          opt('disco', 'Disco'),
          opt('rustic', 'Rustic'),
        ],
      },
      {
        key: 'colourPalette',
        label: 'Colour Palette',
        type: 'text',
        searchFacet: 'colour',
        placeholder: 'e.g. Blush, Ivory, Gold',
      },
      {
        key: 'occasionType',
        label: 'Occasion Type',
        type: 'select',
        searchFacet: 'occasion',
        options: [
          opt('hens-party', 'Hens Party'),
          opt('bridal-shower', 'Bridal Shower'),
          opt('kitchen-tea', 'Kitchen Tea'),
          opt('bachelorette-weekend', 'Bachelorette Weekend'),
        ],
      },
      {
        key: 'quantityIncluded',
        label: 'Quantity Included',
        type: 'number',
      },
      {
        key: 'material',
        label: 'Material',
        type: 'text',
        searchFacet: 'material',
      },
      {
        key: 'condition',
        label: 'Condition',
        type: 'select',
        searchFacet: 'condition',
        options: CONDITION_STANDARD,
      },
      {
        key: 'stylingNotes',
        label: 'Styling Notes',
        type: 'textarea',
      },
      {
        key: 'whatsIncluded',
        label: "What's Included",
        type: 'textarea',
      },
    ],
  },
  {
    id: 'bridal-party',
    name: 'Bridal Party',
    subcategories: [
      { id: 'bridesmaid-dresses', name: 'Bridesmaid Dresses' },
      { id: 'groomsmen-attire', name: 'Groomsmen Attire' },
      { id: 'robes-getting-ready', name: 'Robes & Getting Ready' },
      { id: 'junior-bridal-party', name: 'Junior Bridal Party' },
      { id: 'matching-sets', name: 'Matching Sets' },
      { id: 'bridal-party-accessories', name: 'Bridal Party Accessories' },
    ],
    fields: [
      {
        key: 'designerBrand',
        label: 'Designer / Brand',
        type: 'text',
        searchFacet: 'brand',
      },
      {
        key: 'size',
        label: 'Size',
        type: 'text',
        searchFacet: 'size',
      },
      { key: 'fitNotes', label: 'Fit Notes', type: 'textarea' },
      { key: 'alterationsMade', label: 'Alterations Made', type: 'textarea' },
      {
        key: 'dressStyle',
        label: 'Dress Style',
        type: 'radio',
        searchFacet: 'style',
        options: [
          opt('satin', 'Satin'),
          opt('chiffon', 'Chiffon'),
          opt('a-line', 'A-Line'),
          opt('fitted', 'Fitted'),
          opt('convertible', 'Convertible'),
        ],
      },
      {
        key: 'colour',
        label: 'Colour',
        type: 'text',
        searchFacet: 'colour',
      },
      {
        key: 'fabric',
        label: 'Fabric',
        type: 'text',
        searchFacet: 'material',
      },
      {
        key: 'condition',
        label: 'Condition',
        type: 'select',
        searchFacet: 'condition',
        options: CONDITION_STANDARD,
      },
      {
        key: 'timesWorn',
        label: 'Number of Times Worn',
        type: 'number',
      },
      {
        key: 'dryCleaned',
        label: 'Dry Cleaned',
        type: 'toggle',
      },
      {
        key: 'bridalThemeMatch',
        label: 'Bridal Theme Match',
        type: 'text',
        searchFacet: 'weddingStyle',
      },
      {
        key: 'stylingSuggestions',
        label: 'Styling Suggestions',
        type: 'textarea',
      },
    ],
  },
  {
    id: 'wedding-dress',
    name: 'Wedding Dress',
    subcategories: [
      { id: 'a-line', name: 'A-Line' },
      { id: 'mermaid', name: 'Mermaid' },
      { id: 'ball-gown', name: 'Ball Gown' },
      { id: 'minimalist', name: 'Minimalist' },
      { id: 'boho', name: 'Boho' },
      { id: 'vintage', name: 'Vintage' },
      { id: 'reception-dress', name: 'Reception Dress' },
      { id: 'custom-dress', name: 'Custom Dress' },
    ],
    requiredImageSlots: [
      'Cover Image',
      'Front View',
      'Back View',
      'Detail Closeups',
      'Try-On Photos',
    ],
    fields: [
      {
        key: 'designerBrand',
        label: 'Designer / Brand',
        type: 'text',
        searchFacet: 'brand',
      },
      {
        key: 'dressStyle',
        label: 'Dress Style',
        type: 'text',
        searchFacet: 'style',
      },
      {
        key: 'bridalSize',
        label: 'Bridal Size',
        type: 'text',
        searchFacet: 'size',
      },
      {
        key: 'streetSize',
        label: 'Street Size',
        type: 'text',
      },
      {
        key: 'heightWithHeels',
        label: 'Height Worn With Heels',
        type: 'text',
      },
      { key: 'alterations', label: 'Alterations', type: 'textarea' },
      { key: 'bustMeasurement', label: 'Bust Measurement', type: 'text' },
      { key: 'waistMeasurement', label: 'Waist Measurement', type: 'text' },
      { key: 'hipMeasurement', label: 'Hip Measurement', type: 'text' },
      {
        key: 'fabric',
        label: 'Fabric',
        type: 'text',
        searchFacet: 'material',
      },
      { key: 'neckline', label: 'Neckline', type: 'text' },
      { key: 'sleeveType', label: 'Sleeve Type', type: 'text' },
      { key: 'trainLength', label: 'Train Length', type: 'text' },
      {
        key: 'colourTone',
        label: 'Colour Tone',
        type: 'radio',
        searchFacet: 'colour',
        options: [
          opt('pure-white', 'Pure White'),
          opt('ivory', 'Ivory'),
          opt('champagne', 'Champagne'),
          opt('blush', 'Blush'),
        ],
      },
      { key: 'weddingDate', label: 'Wedding Date', type: 'text' },
      {
        key: 'condition',
        label: 'Condition',
        type: 'select',
        searchFacet: 'condition',
        options: CONDITION_WEDDING_DRESS,
      },
      {
        key: 'professionallyCleaned',
        label: 'Professionally Cleaned',
        type: 'toggle',
      },
      { key: 'damageOrFlaws', label: 'Damage or Flaws', type: 'textarea' },
      { key: 'dressStory', label: 'Love Story / Dress Story', type: 'textarea' },
      { key: 'fitNotes', label: 'Fit Notes', type: 'textarea' },
      { key: 'alterationNotes', label: 'Alteration Notes', type: 'textarea' },
      { key: 'stylingSuggestions', label: 'Styling Suggestions', type: 'textarea' },
    ],
  },
  {
    id: 'shoes',
    name: 'Shoes',
    subcategories: [
      { id: 'bridal-heels', name: 'Bridal Heels' },
      { id: 'flats', name: 'Flats' },
      { id: 'sandals', name: 'Sandals' },
      { id: 'boots', name: 'Boots' },
      { id: 'groom-shoes', name: 'Groom Shoes' },
      { id: 'reception-shoes', name: 'Reception Shoes' },
    ],
    requiredImageSlots: ['Side View', 'Sole View', 'Worn Photos'],
    fields: [
      { key: 'brand', label: 'Brand', type: 'text', searchFacet: 'brand' },
      { key: 'shoeType', label: 'Shoe Type', type: 'text', searchFacet: 'style' },
      { key: 'shoeSize', label: 'Shoe Size', type: 'text', searchFacet: 'size' },
      { key: 'width', label: 'Width', type: 'text' },
      { key: 'heelHeight', label: 'Heel Height', type: 'text' },
      { key: 'colour', label: 'Colour', type: 'text', searchFacet: 'colour' },
      { key: 'material', label: 'Material', type: 'text', searchFacet: 'material' },
      {
        key: 'embellishments',
        label: 'Embellishments',
        type: 'chips',
        options: [
          opt('pearls', 'Pearls'),
          opt('lace', 'Lace'),
          opt('satin', 'Satin'),
          opt('crystal', 'Crystal'),
        ],
      },
      {
        key: 'condition',
        label: 'Condition',
        type: 'select',
        searchFacet: 'condition',
        options: CONDITION_SHOES,
      },
      { key: 'comfortNotes', label: 'Comfort Notes', type: 'textarea' },
      { key: 'wearNotes', label: 'Wear Notes', type: 'textarea' },
      {
        key: 'stylingMatch',
        label: 'Styling Match',
        type: 'text',
        searchFacet: 'weddingStyle',
      },
    ],
  },
  {
    id: 'flowers',
    name: 'Flowers',
    subcategories: [
      { id: 'bridal-bouquets', name: 'Bridal Bouquets' },
      { id: 'bridesmaid-bouquets', name: 'Bridesmaid Bouquets' },
      { id: 'artificial-flowers', name: 'Artificial Flowers' },
      { id: 'ceremony-arrangements', name: 'Ceremony Arrangements' },
      { id: 'table-florals', name: 'Table Florals' },
      { id: 'flower-installations', name: 'Flower Installations' },
      { id: 'dried-flowers', name: 'Dried Flowers' },
    ],
    requiredImageSlots: ['Setup Photos', 'Closeups'],
    fields: [
      { key: 'floralType', label: 'Floral Type', type: 'text', searchFacet: 'style' },
      {
        key: 'weddingStyle',
        label: 'Wedding Style',
        type: 'radio',
        searchFacet: 'weddingStyle',
        options: [
          opt('boho', 'Boho'),
          opt('garden', 'Garden'),
          opt('rustic', 'Rustic'),
          opt('coastal', 'Coastal'),
          opt('modern', 'Modern'),
        ],
      },
      {
        key: 'mainColours',
        label: 'Main Colours',
        type: 'text',
        searchFacet: 'colour',
        placeholder: 'e.g. White, Sage, Dusty Rose',
      },
      {
        key: 'flowerTypes',
        label: 'Flower Types',
        type: 'text',
        placeholder: 'e.g. Roses, Eucalyptus',
      },
      {
        key: 'artificialOrFresh',
        label: 'Artificial or Fresh',
        type: 'select',
        options: [
          opt('artificial', 'Artificial'),
          opt('dried', 'Dried'),
          opt('preserved', 'Preserved'),
        ],
      },
      { key: 'quantityIncluded', label: 'Quantity Included', type: 'number' },
      { key: 'arrangementSize', label: 'Arrangement Size', type: 'text' },
      { key: 'includedPieces', label: 'Included Pieces', type: 'textarea' },
      {
        key: 'condition',
        label: 'Condition',
        type: 'select',
        searchFacet: 'condition',
        options: CONDITION_FLOWERS,
      },
      { key: 'floralStylingNotes', label: 'Floral Styling Notes', type: 'textarea' },
      { key: 'weddingThemeMatch', label: 'Wedding Theme Match', type: 'text', searchFacet: 'theme' },
      { key: 'whatsIncluded', label: "What's Included", type: 'textarea' },
    ],
  },
  {
    id: 'accessories',
    name: 'Accessories',
    subcategories: [
      { id: 'veils', name: 'Veils' },
      { id: 'jewellery', name: 'Jewellery' },
      { id: 'hair-pieces', name: 'Hair Pieces' },
      { id: 'gloves', name: 'Gloves' },
      { id: 'bags', name: 'Bags' },
      { id: 'belts', name: 'Belts' },
      { id: 'bridal-jackets', name: 'Bridal Jackets' },
      { id: 'headpieces', name: 'Headpieces' },
    ],
    requiredImageSlots: ['Detail Shots', 'Worn Photos'],
    fields: [
      { key: 'accessoryType', label: 'Accessory Type', type: 'text', searchFacet: 'style' },
      {
        key: 'designerBrand',
        label: 'Brand / Designer',
        type: 'text',
        searchFacet: 'brand',
      },
      { key: 'colour', label: 'Colour', type: 'text', searchFacet: 'colour' },
      { key: 'material', label: 'Material', type: 'text', searchFacet: 'material' },
      {
        key: 'embellishments',
        label: 'Embellishments',
        type: 'chips',
        options: [
          opt('pearls', 'Pearls'),
          opt('crystals', 'Crystals'),
          opt('lace', 'Lace'),
          opt('satin', 'Satin'),
        ],
      },
      { key: 'length', label: 'Length', type: 'text' },
      { key: 'adjustable', label: 'Adjustable', type: 'toggle' },
      { key: 'fitNotes', label: 'Fit Notes', type: 'textarea' },
      {
        key: 'condition',
        label: 'Condition',
        type: 'select',
        searchFacet: 'condition',
        options: CONDITION_ACCESSORIES,
      },
      { key: 'stylingSuggestions', label: 'Styling Suggestions', type: 'textarea' },
      { key: 'weddingThemeMatch', label: 'Wedding Theme Match', type: 'text', searchFacet: 'theme' },
      { key: 'pairingRecommendations', label: 'Pairing Recommendations', type: 'textarea' },
    ],
  },
];

/**
 * Category tree for config.categoryConfiguration.categories — always derived from
 * WEDLY_CATEGORY_DEFINITIONS, never from the Sharetribe Console "listing-categories.json" asset.
 * Strips the Wedly-specific `fields`/`requiredImageSlots` keys down to the
 * {id, name, subcategories} shape the app's category tree consumers expect.
 */
export const buildWedlyCategoryTree = () =>
  WEDLY_CATEGORY_DEFINITIONS.map(({ id, name, subcategories = [] }) => ({
    id,
    name,
    subcategories: subcategories.map(({ id: subId, name: subName }) => ({
      id: subId,
      name: subName,
    })),
  }));

const normalizeCategoryKey = value =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Find a category node in the Sharetribe category tree by id.
 * @param {Array} hostedCategories from listing-categories.json
 * @param {string} categoryId
 */
export const findHostedCategoryById = (hostedCategories, categoryId) => {
  if (!hostedCategories?.length || !categoryId) {
    return null;
  }
  const search = (nodes = []) => {
    for (const node of nodes) {
      if (node.id === categoryId) {
        return node;
      }
      const found = search(node.subcategories);
      if (found) {
        return found;
      }
    }
    return null;
  };
  return search(hostedCategories);
};

/**
 * Match a Wedly field-definition block to a Sharetribe top-level category.
 */
export const matchWedlyDefinitionToHostedCategory = (definition, hostedCategories) => {
  if (!definition || !hostedCategories?.length) {
    return null;
  }
  return (
    hostedCategories.find(
      c =>
        c.id === definition.id ||
        normalizeCategoryKey(c.name) === normalizeCategoryKey(definition.name)
    ) || null
  );
};

/**
 * Category ids for listingFields.categoryConfig — uses Sharetribe ids when available.
 */
export const resolveSharetribeCategoryIds = (definition, hostedCategories = []) => {
  const hosted = matchWedlyDefinitionToHostedCategory(definition, hostedCategories);
  if (hosted) {
    const subIds = (hosted.subcategories || []).map(s => s.id);
    return [hosted.id, ...subIds];
  }
  return [definition.id, ...(definition.subcategories || []).map(s => s.id)];
};

const schemaTypeForField = field => {
  switch (field.type) {
    case 'select':
    case 'radio':
      return 'enum';
    case 'multiSelect':
    case 'chips':
      return 'multi-enum';
    case 'toggle':
      return 'boolean';
    case 'number':
      return 'long';
    default:
      return 'text';
  }
};

/**
 * Build Sharetribe listingFields entries for Console / config merge (search & filters).
 */
export const buildWedlyListingFields = (hostedCategories = []) => {
  const fields = [];

  WEDLY_CATEGORY_DEFINITIONS.forEach(category => {
    const categoryIds = resolveSharetribeCategoryIds(category, hostedCategories);

    category.fields.forEach(field => {
      const schemaType = schemaTypeForField(field);
      const enumOptions =
        field.options?.map(o => ({ option: o.value, label: o.label })) || undefined;

      const entry = {
        key: field.key,
        scope: 'public',
        schemaType,
        ...(enumOptions ? { enumOptions } : {}),
        categoryConfig: {
          limitToCategoryIds: true,
          categoryIds,
        },
        filterConfig: {
          indexForSearch: !!field.searchFacet,
          showFilter: false,
          label: field.label,
          group: 'secondary',
          ...(schemaType === 'multi-enum' ? { searchMode: 'has_any' } : {}),
        },
        showConfig: {
          label: field.label,
          isDetail: true,
        },
        saveConfig: {
          label: field.label,
          placeholderMessage: field.placeholder,
          isRequired: !!field.required,
        },
      };

      if (schemaType === 'long') {
        entry.numberConfig = { minimum: 0, maximum: 9999 };
      }

      fields.push(entry);
    });
  });

  // Search metadata blob (stored as text JSON for semantic / future AI pipelines)
  fields.push({
    key: 'searchMetadata',
    scope: 'public',
    schemaType: 'text',
    filterConfig: {
      indexForSearch: false,
      showFilter: false,
      label: 'Search metadata',
    },
    showConfig: { label: 'Search metadata', isDetail: false },
    saveConfig: { label: 'Search metadata', isRequired: false },
  });

  return fields;
};

export const isWedlyListingConfigEnabled = () => {
  if (process.env.REACT_APP_WEDLY_LISTING_CONFIG === 'true') {
    return true;
  }
  if (process.env.REACT_APP_WEDLY_LISTING_CONFIG === 'false') {
    return false;
  }
  return process.env.NODE_ENV === 'development';
};
